import '@fontsource-variable/inter';
import '@fontsource/space-grotesk/700.css';
import './styles/global.css';
import { StrictMode } from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { App } from './App';
import { localeOfHomePath, seoPageFor } from '@snakefeast/shared';
import { preloadContent } from './content/routes';
import { detectLocale, setLocale } from './i18n';
import { initAnalytics } from './lib/analytics';
import { captureInstallPrompt } from './lib/install';
import { afterStartup, trackKeyboardInset } from './lib/platform';
import { initPortal, loadingFinished, onPortalSettings, PORTAL, portalLocale, portalMuted } from './lib/portal';
import { setPortalMuted } from './lib/sound';
import { registerServiceWorker } from './lib/push';

const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
if (dsn) {
  // Loaded once the page has settled; errors before that are rare and not worth the bytes.
  afterStartup(() =>
    void import('@sentry/react').then((Sentry) =>
      Sentry.init({ dsn, environment: import.meta.env.MODE, release: __APP_VERSION__, tracesSampleRate: 0.05, replaysSessionSampleRate: 0 }),
    ),
  );
}

captureInstallPrompt();
initAnalytics();
trackKeyboardInset();
// After a deploy, a tab still on the old build asks for chunks that no longer exist: load the new build.
// Once per minute at most, so a real outage can't turn into a reload loop.
window.addEventListener('vite:preloadError', (e) => {
  const KEY = 'snakefeast.chunkReloadAt';
  try {
    const last = Number(sessionStorage.getItem(KEY) ?? 0);
    if (Date.now() - last < 60_000) return;
    sessionStorage.setItem(KEY, String(Date.now()));
  } catch {
    return; // No storage: nothing would stop a loop.
  }
  e.preventDefault();
  window.location.reload();
});

// Push notifications only; registering early lets the app show as installable too.
window.addEventListener('load', () => void registerServiceWorker());

const path = window.location.pathname.replace(/\/+$/, '') || '/';
// Portals: the SDK first, so its language (and mute setting) apply from the first screen.
const portalReady = initPortal().then(() => {
  if (!PORTAL) return;
  document.body.style.userSelect = 'none';
  document.body.style.setProperty('-webkit-user-select', 'none');
  setPortalMuted(portalMuted());
  onPortalSettings(() => setPortalMuted(portalMuted()));
});
// A language's own landing URL ("/hi", "/es"…) opens in that language, whatever was chosen before.
const urlLocale = PORTAL ? null : localeOfHomePath(path);
void Promise.all([portalReady.then(() => (urlLocale ? setLocale(urlLocale, { remember: false }) : setLocale(detectLocale(portalLocale())))), preloadContent(path)]).finally(() => {
  const root = document.getElementById('root')!;
  const app = (
    <StrictMode>
      <App />
    </StrictMode>
  );
  // Content pages arrive pre-rendered: adopt that HTML instead of redrawing it (no repaint, no layout shift).
  if (root.firstElementChild && seoPageFor(path)) hydrateRoot(root, app);
  else createRoot(root).render(app);
  loadingFinished();
});
